import * as THREE from 'three';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import filmLookFrag from '../../glsl/film_look_frag.glsl';
import { buildLUTData } from './cameraRawGrade.js';

/**
 * Film-emulation grade — a Lightroom Classic / Camera Raw preset applied as a
 * baked 3D LUT.
 *
 * {@link module:fx/cameraRawGrade} compiles the preset's sliders and tone curves
 * into a colour-to-colour function and evaluates it over a `size³` lattice; this
 * uploads that lattice as a `Data3DTexture` and the pass is then one hardware
 * trilinear lookup per pixel, independent of how complicated the preset was.
 * Building a 33³ lattice costs ~15 ms on the main thread, so it happens once at
 * construction and never per frame.
 *
 * ── Why it sits where it does ────────────────────────────────────────────────
 * App inserts this *after* OutputPass. A Lightroom preset is a grade on a
 * finished, display-referred image — its tone curves are drawn on a 0–255 axis
 * and its black and white points mean nothing against unbounded HDR values — so
 * the LUT is built in sRGB and has to be sampled there. It stays *before* the
 * NVG pass, which is a sensor simulation rather than a look and should have the
 * last word on the image whenever the goggles are down.
 *
 * ── Why half float ───────────────────────────────────────────────────────────
 * An 8-bit LUT is the usual `.cube` convention, but this grade lifts the deep
 * shadows onto a steep part of the curve, where 1/255 steps in the lattice turn
 * into visible banding across a large smooth sky. Half float costs 287 KB at
 * 33³ and removes the question; linear filtering of half-float 3D textures is
 * core in WebGL2, which is the only target three r185 has.
 */
export default class FilmLookPass extends ShaderPass {
  /**
   * @param {object}  preset     Camera Raw preset (see `fx/presets/`).
   * @param {number}  [size=33]  LUT lattice edge length.
   * @param {number}  [strength] Initial blend, 0..1.
   */
  constructor({ preset, size = 33, strength = 1 }) {
    super(new THREE.ShaderMaterial({
      name: 'FilmLookShader',
      glslVersion: THREE.GLSL3,
      uniforms: {
        tDiffuse:  { value: null },
        uLut:      { value: null },
        uLutSize:  { value: size },
        uStrength: { value: strength },
      },
      vertexShader: `
        out vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
      `,
      fragmentShader: filmLookFrag,
    }));

    this._size = size;
    this.setPreset(preset);
  }

  /** Rebuild the LUT from a different preset. Disposes the previous texture. */
  setPreset(preset) {
    this.preset = preset;
    this.lut?.dispose();

    const size = this._size;
    const floats = buildLUTData(preset, size);
    const half = new Uint16Array(floats.length);
    for (let i = 0; i < floats.length; i++) half[i] = THREE.DataUtils.toHalfFloat(floats[i]);

    const lut = new THREE.Data3DTexture(half, size, size, size);
    lut.format = THREE.RGBAFormat;
    lut.type = THREE.HalfFloatType;
    lut.minFilter = THREE.LinearFilter;
    lut.magFilter = THREE.LinearFilter;
    // Clamp, not wrap: a colour at the very top of a channel must land on the
    // last lattice point, not fold back round to the first.
    lut.wrapS = THREE.ClampToEdgeWrapping;
    lut.wrapT = THREE.ClampToEdgeWrapping;
    lut.wrapR = THREE.ClampToEdgeWrapping;
    // The LUT holds grade outputs, not colours to be decoded — no transfer
    // function must be applied on the way out of the sampler.
    lut.colorSpace = THREE.NoColorSpace;
    lut.needsUpdate = true;

    this.lut = lut;
    this.uniforms.uLut.value = lut;
    this.uniforms.uLutSize.value = size;
  }

  /** Blend between the ungraded frame (0) and the full preset (1). */
  setStrength(v) {
    this.uniforms.uStrength.value = Math.min(1, Math.max(0, v));
  }

  dispose() {
    this.lut?.dispose();
    this.material.dispose();
  }
}
